import { useEffect, useRef, useState } from "react";
import { Loader2, Send } from "lucide-react";

const time = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

export const ChatThread = ({ conversation, staffView, maxLength = 600, onSend }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const messages = conversation?.messages || [];

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "nearest" });
  }, [messages.length]);

  const submit = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    try {
      await onSend(body);
      setText("");
    } finally {
      setBusy(false);
    }
  };

  const closed = conversation?.status === "fechado";

  return (
    <div data-testid="chat-thread"
         className="flex h-[calc(100vh-15rem)] min-h-[20rem] flex-col overflow-hidden rounded-lg border border-[#141A26] chat-surface">
      <div className="flex-1 space-y-2 overflow-y-auto p-4">
        {messages.length === 0 ? (
          <p className="text-sm chat-meta">Ainda não existem mensagens nesta conversa.</p>
        ) : null}
        {messages.map((m) => {
          const mine = staffView ? m.sender_role !== "user" : m.sender_role === "user";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}
                 data-testid={`chat-message-${m.id}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm shadow-sm ${
                mine ? "chat-bubble-mine rounded-br-sm" : "chat-bubble-them rounded-bl-sm"
              }`}>
                <p className="mb-1 text-[11px] font-medium chat-meta">{m.sender_name}</p>
                <p className="whitespace-pre-wrap break-words">{m.body}</p>
                <p className="mt-1 text-right font-mono text-[10px] chat-meta">{time(m.created_at)}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <form onSubmit={submit} className="flex items-center gap-2 border-t border-[#141A26] p-2 chat-card">
        <textarea
          data-testid="chat-input"
          value={text}
          disabled={closed}
          maxLength={maxLength}
          onChange={(e) => setText(e.target.value.slice(0, maxLength))}
          placeholder={closed ? "Conversa fechada" : `Escreva a sua mensagem (${maxLength - text.length} caracteres)`}
          rows={1}
          className="max-h-24 flex-1 resize-none rounded-2xl border px-4 py-2 text-sm outline-none chat-field"
        />
        <span className="hidden font-mono text-[10px] chat-meta sm:block" data-testid="chat-counter">
          {text.length}/{maxLength}
        </span>
        <button
          type="submit"
          data-testid="chat-send"
          disabled={busy || closed || !text.trim()}
          className="flex h-10 shrink-0 items-center gap-2 rounded-full bg-[#00A884] px-4 text-sm font-medium text-white transition-colors hover:bg-[#029072] disabled:opacity-40"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Enviar
        </button>
      </form>
    </div>
  );
};
